import React, { useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function AdminPanel() {
  const [apiToken, setApiToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState('links');

  // Board/Item/Column selection
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boardDetails, setBoardDetails] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Link settings
  const [expiresInHours, setExpiresInHours] = useState(72);
  const [description, setDescription] = useState('');
  const [timeRequired, setTimeRequired] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');

  // Active links
  const [activeLinks, setActiveLinks] = useState([]);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [tplStep, setTplStep] = useState(1);
  const [tplBoard, setTplBoard] = useState(null);
  const [tplBoardDetails, setTplBoardDetails] = useState(null);
  const [tplSourceColumns, setTplSourceColumns] = useState([]);
  const [tplTargetColumn, setTplTargetColumn] = useState('');
  const [tplDescription, setTplDescription] = useState('');
  const [tplExpires, setTplExpires] = useState(720);
  const [tplTimeRequired, setTplTimeRequired] = useState(false);
  const [tplSuccess, setTplSuccess] = useState('');

  // Loading/Error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'x-monday-token': apiToken
  }), [apiToken]);

  // Auth
  const handleAuth = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/monday/boards`, { headers: headers() });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setBoards(data);
      setIsAuthenticated(true);
      loadActiveLinks();
      loadTemplates();
    } catch (err) {
      setError('Verbindung fehlgeschlagen: ' + err.message);
    }
    setLoading(false);
  };

  // Load boards
  const loadBoards = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/monday/boards`, { headers: headers() });
      const data = await res.json();
      if (!data.error) setBoards(data);
    } catch (err) { /* ignore */ }
  };

  // Load active links
  const loadActiveLinks = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/monday/active-links`);
      const data = await res.json();
      setActiveLinks(data);
    } catch (err) { /* ignore */ }
  };

  // Load templates
  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/templates`);
      const data = await res.json();
      setTemplates(data);
    } catch (err) { /* ignore */ }
  };

  // Select board
  const handleSelectBoard = async (boardId) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/monday/board/${boardId}`, { headers: headers() });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSelectedBoard(boardId);
      setBoardDetails(data);
      setItems(data.items || []);
      setSelectedItems([]);
      setSelectedColumns([]);
      setStep(2);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Toggle item selection
  const toggleItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Toggle column selection
  const toggleColumn = (colId) => {
    setSelectedColumns(prev =>
      prev.includes(colId)
        ? prev.filter(id => id !== colId)
        : [...prev, colId]
    );
  };

  // Select all items
  const selectAllItems = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(i => i.id));
    }
  };

  // Generate external link
  const handleGenerateLink = async () => {
    setLoading(true);
    setError('');
    setGeneratedLink('');
    try {
      const columnTitles = {};
      if (boardDetails?.columns) {
        boardDetails.columns.forEach(col => {
          if (selectedColumns.includes(col.id)) {
            columnTitles[col.id] = col.title;
          }
        });
      }

      const res = await fetch(`${API_BASE}/api/monday/create-link`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          boardId: selectedBoard,
          boardName: boardDetails?.name || '',
          itemIds: selectedItems,
          columnIds: selectedColumns,
          columnTitles,
          expiresInHours: parseInt(expiresInHours),
          description,
          timeRequired
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setGeneratedLink(data.url);
      loadActiveLinks();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopyFeedback('✓ Kopiert!');
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  // Revoke link
  const handleRevoke = async (tokenId) => {
    try {
      await fetch(`${API_BASE}/api/monday/revoke/${tokenId}`, { method: 'DELETE' });
      loadActiveLinks();
    } catch (err) { /* ignore */ }
  };

  // Template: Select board
  const handleTplSelectBoard = async (boardId) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/monday/board/${boardId}`, { headers: headers() });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setTplBoard(boardId);
      setTplBoardDetails(data);
      setTplSourceColumns([]);
      setTplTargetColumn('');
      setTplStep(2);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Template: Toggle source column
  const toggleTplSourceColumn = (colId) => {
    setTplSourceColumns(prev =>
      prev.includes(colId)
        ? prev.filter(id => id !== colId)
        : [...prev, colId]
    );
  };

  // Template: Create
  const handleCreateTemplate = async () => {
    setLoading(true);
    setError('');
    setTplSuccess('');
    try {
      const sourceColumnTitles = {};
      tplBoardDetails.columns.forEach(col => {
        if (tplSourceColumns.includes(col.id)) {
          sourceColumnTitles[col.id] = col.title;
        }
      });
      const targetCol = tplBoardDetails.columns.find(c => c.id === tplTargetColumn);

      const res = await fetch(`${API_BASE}/api/templates`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          boardId: tplBoard,
          boardName: tplBoardDetails.name,
          sourceColumnIds: tplSourceColumns,
          sourceColumnTitles,
          targetColumnId: tplTargetColumn,
          targetColumnTitle: targetCol?.title || tplTargetColumn,
          description: tplDescription,
          expiresInHours: parseInt(tplExpires),
          timeRequired: tplTimeRequired
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setTplSuccess(`Vorlage erstellt! Neue Items in "${tplBoardDetails.name}" werden automatisch verarbeitet.`);
      setTplStep(1);
      setTplBoard(null);
      setTplBoardDetails(null);
      setTplSourceColumns([]);
      setTplTargetColumn('');
      setTplDescription('');
      setTplTimeRequired(false);
      loadTemplates();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const toggleTemplateActive = async (id, currentActive) => {
    try {
      await fetch(`${API_BASE}/api/templates/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive })
      });
      loadTemplates();
    } catch (err) { /* ignore */ }
  };

  const deleteTemplate = async (id) => {
    try {
      await fetch(`${API_BASE}/api/templates/${id}`, { method: 'DELETE' });
      loadTemplates();
    } catch (err) { /* ignore */ }
  };

  // Reset
  const resetAll = () => {
    setStep(1);
    setSelectedBoard(null);
    setBoardDetails(null);
    setItems([]);
    setSelectedItems([]);
    setSelectedColumns([]);
    setGeneratedLink('');
    setTemplates([]);
  };

  // Editable column types (die für externe sinnvoll sind)
  const editableTypes = ['date', 'text', 'long_text', 'numbers', 'status', 'color', 'email', 'phone', 'link', 'hour', 'timeline', 'file', 'boolean', 'checkbox'];

  // --- TOKEN INPUT SCREEN ---
  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="token-input-wrapper">
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
          <h2>Monday.com Verbinden</h2>
          <p>Geben Sie Ihren Monday.com API Token ein, um zu starten.<br />
            Sie finden diesen unter <strong>Profil → Admin → API</strong>.</p>
          <input
            type="password"
            value={apiToken}
            onChange={e => setApiToken(e.target.value)}
            placeholder="API Token eingeben..."
            onKeyPress={e => e.key === 'Enter' && handleAuth()}
          />
          {error && <div className="alert alert-error">{error}</div>}
          <button className="btn btn-primary" onClick={handleAuth} disabled={loading || !apiToken}>
            {loading ? 'Verbinde...' : '🚀 Verbinden'}
          </button>
        </div>
      </div>
    );
  }

  // --- MAIN UI ---
  return (
    <div className="admin-container">
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Monday.com External Update</h1>
          <p>Erstellen Sie sichere Links für externe Personen oder automatische Vorlagen.</p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-nav">
          <button
            className={`tab-btn ${activeTab === 'links' ? 'active' : ''}`}
            onClick={() => { setActiveTab('links'); loadBoards(); }}
          >
            🔗 Einzelne Links
          </button>
          <button
            className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
            onClick={() => { setActiveTab('templates'); loadBoards(); loadTemplates(); }}
          >
            ⚡ Vorlagen
          </button>
        </div>

        {/* ===== TEMPLATES TAB ===== */}
        {activeTab === 'templates' && (
          <>
            {error && <div className="alert alert-error">{error}</div>}
            {tplSuccess && <div className="alert alert-success">{tplSuccess}</div>}

            <div className="card">
              <h2>⚡ Neue Vorlage erstellen</h2>
              <p style={{ color: '#676879', fontSize: '14px', marginBottom: '16px' }}>
                Vorlagen erstellen automatisch einen externen Link für jedes neue Item in einem Board.
              </p>

              {/* Step indicators */}
              <div className="steps-indicator" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <div className={`step-badge ${tplStep >= 1 ? 'active' : ''}`}>
                  <span className="step-number">1</span> Board
                </div>
                <div className={`step-badge ${tplStep >= 2 ? 'active' : ''}`}>
                  <span className="step-number">2</span> Spalten
                </div>
                <div className={`step-badge ${tplStep >= 3 ? 'active' : ''}`}>
                  <span className="step-number">3</span> Einstellungen
                </div>
              </div>

              {/* Step 1: Board auswählen */}
              {tplStep === 1 && (
                <div className="selection-list">
                  {boards.map(board => (
                    <div key={board.id} className="selection-item" onClick={() => handleTplSelectBoard(board.id)} style={{ cursor: 'pointer' }}>
                      <span style={{ fontSize: '18px' }}>📊</span>
                      <span className="item-name">{board.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Step 2: Spalten wählen */}
              {tplStep === 2 && tplBoardDetails && (
                <>
                  <p style={{ fontWeight: 600, marginBottom: '8px' }}>Quell-Spalten (vom Externen auszufüllen):</p>
                  <div className="selection-list" style={{ marginBottom: '16px' }}>
                    {tplBoardDetails.columns
                      .filter(col => editableTypes.includes(col.type))
                      .map(col => (
                        <div
                          key={col.id}
                          className={`selection-item ${tplSourceColumns.includes(col.id) ? 'selected' : ''}`}
                          onClick={() => toggleTplSourceColumn(col.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <input type="checkbox" checked={tplSourceColumns.includes(col.id)} readOnly />
                          <span className="item-name">{col.title}</span>
                          <span className="item-type">{col.type}</span>
                        </div>
                      ))}
                  </div>

                  <p style={{ fontWeight: 600, marginBottom: '8px' }}>Ziel-Spalte (für den Link):</p>
                  <div className="selection-list">
                    {tplBoardDetails.columns
                      .filter(col => col.type === 'text' || col.type === 'link' || col.type === 'long_text')
                      .map(col => (
                        <div
                          key={col.id}
                          className={`selection-item ${tplTargetColumn === col.id ? 'selected' : ''}`}
                          onClick={() => setTplTargetColumn(col.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <input type="radio" checked={tplTargetColumn === col.id} readOnly />
                          <span className="item-name">{col.title}</span>
                          <span className="item-type">{col.type}</span>
                        </div>
                      ))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button className="btn btn-outline" onClick={() => setTplStep(1)}>← Zurück</button>
                    <button
                      className="btn btn-primary"
                      onClick={() => setTplStep(3)}
                      disabled={tplSourceColumns.length === 0 || !tplTargetColumn}
                    >
                      Weiter →
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Einstellungen */}
              {tplStep === 3 && (
                <>
                  <div className="form-group">
                    <label>Beschreibung für externe Personen</label>
                    <textarea
                      value={tplDescription}
                      onChange={e => setTplDescription(e.target.value)}
                      placeholder="z.B. Bitte tragen Sie das TÜV-Prüfungsdatum ein..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Link-Gültigkeit</label>
                    <select value={tplExpires} onChange={e => setTplExpires(e.target.value)}>
                      <option value={72}>3 Tage</option>
                      <option value={168}>1 Woche</option>
                      <option value={720}>30 Tage</option>
                      <option value={2160}>90 Tage</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={tplTimeRequired}
                        onChange={e => setTplTimeRequired(e.target.checked)}
                        style={{ width: '18px', height: '18px', accentColor: '#0073ea' }}
                      />
                      Uhrzeit bei Datumsfeldern verpflichtend
                    </label>
                    <span style={{ fontSize: '12px', color: '#676879', marginTop: '4px', display: 'block' }}>
                      Wenn aktiviert, muss der externe Nutzer bei Datumsfeldern auch eine Uhrzeit angeben.
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-outline" onClick={() => setTplStep(2)}>← Zurück</button>
                    <button className="btn btn-primary" onClick={handleCreateTemplate} disabled={loading}>
                      {loading ? 'Wird erstellt...' : '⚡ Vorlage aktivieren'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Active Templates */}
            {templates.length > 0 && (
              <div className="card" style={{ marginTop: '24px' }}>
                <h2>⚡ Aktive Vorlagen</h2>
                <table className="links-table">
                  <thead>
                    <tr>
                      <th>Board</th>
                      <th>Spalten</th>
                      <th>Status</th>
                      <th>Verarbeitet</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map(tpl => (
                      <tr key={tpl.id}>
                        <td>{tpl.boardName}</td>
                        <td>{Object.values(tpl.sourceColumnTitles || {}).join(', ')}</td>
                        <td>
                          <span className={`status-badge ${tpl.active ? 'active' : 'inactive'}`}>
                            {tpl.active ? '✓ Aktiv' : '⏸ Pausiert'}
                          </span>
                        </td>
                        <td>{tpl.processedCount} Items</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => toggleTemplateActive(tpl.id, tpl.active)}
                          >
                            {tpl.active ? '⏸ Pause' : '▶ Start'}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteTemplate(tpl.id)}
                            style={{ marginLeft: '4px' }}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ===== LINKS TAB ===== */}
        {activeTab === 'links' && (
          <>
            {/* Step indicators */}
            <div className="steps-indicator" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <div
                className={`step-badge ${step >= 1 ? 'active' : ''}`}
                onClick={() => { resetAll(); loadBoards(); }}
                style={{ cursor: 'pointer' }}>
                <span className="step-number">1</span> Board wählen
              </div>
              <div
                className={`step-badge ${step >= 2 ? 'active' : ''}`}
                style={{ cursor: step >= 2 ? 'pointer' : 'default' }}>
                <span className="step-number">2</span> Items & Spalten
              </div>
              <div
                className={`step-badge ${step >= 3 ? 'active' : ''}`}
                onClick={() => selectedItems.length > 0 && selectedColumns.length > 0 && setStep(3)}
                style={{ cursor: selectedItems.length > 0 && selectedColumns.length > 0 ? 'pointer' : 'default' }}>
                <span className="step-number">3</span> Link erstellen
              </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            {/* STEP 1: Board Selection */}
            {step === 1 && (
              <div className="card">
                <h2>Board auswählen</h2>
                <p style={{ color: '#676879', fontSize: '14px', marginBottom: '16px' }}>
                  Wählen Sie das Board, das externe Personen bearbeiten sollen.
                </p>
                {loading ? (
                  <div className="loading"><span className="spinner"></span> Boards werden geladen...</div>
                ) : (
                  <div className="selection-list">
                    {boards.map(board => (
                      <div
                        key={board.id}
                        className="selection-item"
                        onClick={() => handleSelectBoard(board.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span style={{ fontSize: '18px' }}>📊</span>
                        <span className="item-name">{board.name}</span>
                        <span className="item-type">ID: {board.id}</span>
                      </div>
                    ))}
                    {boards.length === 0 && (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#676879' }}>
                        Keine Boards gefunden
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Select Items & Columns */}
            {step === 2 && boardDetails && (
              <>
                <div className="card">
                  <h2>Items auswählen (z.B. FINs)</h2>
                  <p style={{ color: '#676879', fontSize: '14px', marginBottom: '12px' }}>
                    Board: <strong>{boardDetails.name}</strong> — Wählen Sie die Zeilen, die bearbeitet werden dürfen.
                  </p>
                  <div style={{ marginBottom: '8px' }}>
                    <button className="btn btn-sm btn-outline" onClick={selectAllItems}>
                      {selectedItems.length === items.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                    <span style={{ marginLeft: '8px', fontSize: '13px', color: '#676879' }}>
                      {selectedItems.length} von {items.length} ausgewählt
                    </span>
                  </div>
                  <div className="selection-list">
                    {items.map(item => (
                      <div
                        key={item.id}
                        className={`selection-item ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                        onClick={() => toggleItem(item.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <input type="checkbox" checked={selectedItems.includes(item.id)} readOnly />
                        <span className="item-name">{item.name}</span>
                        <span className="item-type">ID: {item.id}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card" style={{ marginTop: '16px' }}>
                  <h2>Spalten auswählen</h2>
                  <p style={{ color: '#676879', fontSize: '14px', marginBottom: '12px' }}>
                    Welche Felder sollen externe Personen bearbeiten können?
                  </p>
                  <div className="selection-list">
                    {boardDetails.columns
                      .filter(col => editableTypes.includes(col.type))
                      .map(col => (
                        <div
                          key={col.id}
                          className={`selection-item ${selectedColumns.includes(col.id) ? 'selected' : ''}`}
                          onClick={() => toggleColumn(col.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <input type="checkbox" checked={selectedColumns.includes(col.id)} readOnly />
                          <span className="item-name">{col.title}</span>
                          <span className="item-type">{col.type}</span>
                        </div>
                      ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button className="btn btn-outline" onClick={() => setStep(1)}>← Zurück</button>
                    <button
                      className="btn btn-primary"
                      onClick={() => setStep(3)}
                      disabled={selectedItems.length === 0 || selectedColumns.length === 0}
                    >
                      Weiter →
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* STEP 3: Generate Link */}
            {step === 3 && (
              <div className="card">
                <h2>Externen Link erstellen</h2>

                <div className="alert alert-info">
                  <strong>{selectedItems.length}</strong> Item(s) und <strong>{selectedColumns.length}</strong> Spalte(n) ausgewählt
                </div>

                <div className="form-group">
                  <label>Beschreibung (optional)</label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="z.B. Bitte tragen Sie das TÜV-Prüfungsdatum ein..."
                  />
                </div>

                <div className="form-group">
                  <label>Link gültig für (Stunden)</label>
                  <select value={expiresInHours} onChange={e => setExpiresInHours(e.target.value)}>
                    <option value={24}>24 Stunden (1 Tag)</option>
                    <option value={72}>72 Stunden (3 Tage)</option>
                    <option value={168}>168 Stunden (1 Woche)</option>
                    <option value={720}>720 Stunden (30 Tage)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={timeRequired}
                      onChange={e => setTimeRequired(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#0073ea' }}
                    />
                    Uhrzeit bei Datumsfeldern verpflichtend
                  </label>
                  <span style={{ fontSize: '12px', color: '#676879', marginTop: '4px', display: 'block' }}>
                    Wenn aktiviert, muss der externe Nutzer bei Datumsfeldern auch eine Uhrzeit angeben.
                  </span>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleGenerateLink}
                  disabled={loading}
                >
                  {loading ? 'Wird generiert...' : '🔗 Link generieren'}
                </button>

                {generatedLink && (
                  <div className="link-display">
                    <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                      ✅ Link erfolgreich erstellt!
                    </div>
                    <div className="link-url">{generatedLink}</div>
                    <div className="link-actions">
                      <button className="btn btn-sm btn-primary" onClick={copyToClipboard}>
                        📋 Kopieren
                      </button>
                      {copyFeedback && <span className="copy-feedback">{copyFeedback}</span>}
                    </div>
                  </div>
                )}

                <button className="btn btn-outline" onClick={() => setStep(2)} style={{ marginTop: '12px' }}>
                  ← Zurück
                </button>
              </div>
            )}

            {/* Active Links */}
            {activeLinks.length > 0 && (
              <div className="card" style={{ marginTop: '24px' }}>
                <h2>🔗 Aktive Links</h2>
                <table className="links-table">
                  <thead>
                    <tr>
                      <th>Board</th>
                      <th>Items</th>
                      <th>Spalten</th>
                      <th>Erstellt</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeLinks.map(link => (
                      <tr key={link.tokenId}>
                        <td>{link.boardName || link.boardId}</td>
                        <td>{link.itemCount}</td>
                        <td>{link.columnCount}</td>
                        <td>{new Date(link.createdAt).toLocaleDateString('de-DE')}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRevoke(link.tokenId)}
                          >
                            🗑 Widerrufen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
